import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import type { IRowNode } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';

import { PageTitle } from '~/components/common';
import { DeliveryAgencyExcelDownloadModal } from '~/components/delivery-agency';
import {
  DeliveryAgencyTrackingCancelFailModal,
  DeliveryAgencyTrackingCreateShippingLabelModal,
  DeliveryAgencyTrackingDetailModal,
  DeliveryAgencyTrackingFilter,
  DeliveryAgencyTrackingTable,
} from '~/components/delivery-agency/tracking';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetShippingLabelsQuery,
  useGetShippingLabelsSearchConditionsQuery,
  useGetShippingLabelsShippingLabelIdMutation,
  usePostShippingLabelsCancelMutation,
  usePostShippingLabelsDownloadMutation,
  usePostShippingLabelsPrintMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf, renderFilterErrorMessage } from '~/utils/common';
import { formatDate, getDateByOffset } from '~/utils/date';

import {
  BREAD_CRUMB,
  DELIVERY_AGENCY,
  DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE,
  DELIVERY_AGENCY_SORT_BY,
  DELIVERY_AGENCY_TRACKING_PRINT_STATUS,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  OPTION_ALL,
  PATH,
  TOAST_MESSAGE,
  TypeDeliveryAgencySortBy,
} from '~/constants';

import type {
  TypeDeliveryAgencyShippingLabel,
  TypeDeliveryAgencyShippingLabelDetail,
  TypeDeliveryAgencyTrackingFilterOptions,
  TypeDeliveryAgencyTrackingFilterValues,
  TypeDeliveryAgencyTrackingFilterValuesStatus,
  TypeGetShippingLabelsParams,
  TypePostShippingLabelsCancelResponse,
  TypePostShippingLabelsDownloadParams,
} from '~/types/delivery-agency.types';

const EMPTY_FILTER_OPTIONS: TypeDeliveryAgencyTrackingFilterOptions = {
  searchKeywordTypes: [],
  trackingStatuses: [],
  deliveryFailureReasonCategories: [],
  deliveryTypes: [],
  courierCodes: [],
};

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyTrackingFilterValues = {
  keywordType: null,
  keyword: '',
  deliveryDate: getDateByOffset(1, 'day'),
  trackingStatuses: [OPTION_ALL],
  deliveryFailureReasonCategories: [OPTION_ALL],
  trackingPrintStatus: OPTION_ALL,
  deliveryType: OPTION_ALL,
  courier: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  },
  sortBy: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC,
};

const DeliveryAgencyTrackingPage = () => {
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact>(null);
  const [searchParams] = useSearchParams();

  const { data: searchFilterOptions, isError: isSearchConditionsError } = useGetShippingLabelsSearchConditionsQuery({
    silent: true,
  });
  const shippingLabelDetailMutation = useGetShippingLabelsShippingLabelIdMutation();
  const shippingLabelsPrintMutation = usePostShippingLabelsPrintMutation();
  const shippingLabelsCancelMutation = usePostShippingLabelsCancelMutation();
  const shippingLabelsDownloadMutation = usePostShippingLabelsDownloadMutation();

  const [searchFilterValues, setSearchFilterValues] =
    useState<TypeDeliveryAgencyTrackingFilterValues>(INITIAL_SEARCH_FILTER_VALUES);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] =
    useState<TypeDeliveryAgencyTrackingFilterValuesStatus>({
      keyword: 'default',
      deliveryDate: 'default',
    });

  const [shippingLabelsQueryParams, setShippingLabelsQueryParams] = useState<TypeGetShippingLabelsParams | null>(null);
  const shippingLabelsQuery = useGetShippingLabelsQuery({
    params: shippingLabelsQueryParams ?? ({} as TypeGetShippingLabelsParams),
    enabled: !!shippingLabelsQueryParams,
  });

  const [selectedItems, setSelectedItems] = useState<TypeDeliveryAgencyShippingLabel[]>([]);
  const [trackingDetailData, setTrackingDetailData] = useState<TypeDeliveryAgencyShippingLabelDetail | null>(null);
  const [cancelFailData, setCancelFailData] = useState<NonNullable<
    TypePostShippingLabelsCancelResponse['data']
  > | null>(null);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });
  const { isDownloadReady, markDownloadReady, checkFilterChange, searchedFilter } = useDownloadReady({
    initialFilter: INITIAL_SEARCH_FILTER_VALUES,
  });

  const trackingDetailModal = useModal();

  const trackingExcelDownloadModal = useModal();

  const createShippingLabelModal = useModal();

  const cancelFailModal = useModal();

  const resetSelection = () => {
    gridRef.current?.api?.deselectAll();
    setSelectedItems([]);
  };

  useEffect(() => {
    if (searchFilterOptions) {
      const deliveryDateParam = searchParams.get('deliveryDate');
      const deliveryTypeParam = searchParams.get('deliveryType');
      const shippingLabelStatusParam = searchParams.get('shippingLabelStatus');

      const deliveryTypeOption = deliveryTypeParam
        ? searchFilterOptions.deliveryTypes.find((opt) => opt.value === deliveryTypeParam) ?? OPTION_ALL
        : OPTION_ALL;

      const trackingStatusOption = shippingLabelStatusParam
        ? searchFilterOptions.trackingStatuses.find((opt) => opt.value === shippingLabelStatusParam)
        : null;

      setSearchFilterValues((prev) => {
        const updatedValues: TypeDeliveryAgencyTrackingFilterValues = {
          ...prev,
          keywordType: searchFilterOptions.searchKeywordTypes[0] ?? null,
          ...(deliveryDateParam ? { deliveryDate: new Date(deliveryDateParam) } : {}),
          ...(deliveryTypeParam ? { deliveryType: deliveryTypeOption } : {}),
          ...(trackingStatusOption ? { trackingStatuses: [trackingStatusOption] } : {}),
        };

        if (deliveryDateParam || deliveryTypeParam) {
          handleSearch(updatedValues);
        }

        return updatedValues;
      });
    }
  }, [searchFilterOptions, searchParams]);

  useEffect(() => {
    if (isSearchConditionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isSearchConditionsError]);

  useEffect(() => {
    if (!shippingLabelsQuery.data) return;

    const { pagination, data } = shippingLabelsQuery.data;
    const updatedFilterValues = {
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        totalCount: pagination.listTotal,
        pageSize: pagination.listView,
        currentPage: pagination.cursor,
      },
    };
    setSearchFilterValues(updatedFilterValues);
    markDownloadReady({ filterValue: updatedFilterValues, hasData: data.length > 0 });
  }, [shippingLabelsQuery.dataUpdatedAt]);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  const handleSearch = (filterValues: TypeDeliveryAgencyTrackingFilterValues = searchFilterValues) => {
    if (filterValues.keywordType?.value === DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE && !filterValues.deliveryDate) {
      dialogService.addDialog({ children: '배송예정일을 입력 후 검색해 주세요.' });
      setSearchFilterValuesStatus((prev) => ({ ...prev, deliveryDate: 'error' }));
      return;
    }

    if (filterValues.keyword.trim() === '' && !filterValues.deliveryDate) {
      dialogService.addDialog({
        children:
          '[운송장 번호, 출고번호, 판매처 주문번호, 수령자명, 주문자명, 배송예정일] 중 1개 이상 입력 후 검색해주세요.',
      });
      return;
    }

    const params: TypeGetShippingLabelsParams = {
      searchKeywordType: filterValues.keywordType?.value ?? '',
      searchKeyword: filterValues.keyword,
      ...addIf(formatDate(filterValues.deliveryDate, 'YYYY-MM-DD'), 'estimatedDeliveryDate'),
      ...addIf(
        filterValues.trackingStatuses.some((option) => option.value !== OPTION_ALL.value),
        'shippingLabelStatuses',
        filterValues.trackingStatuses
          .filter((option) => option.value !== OPTION_ALL.value)
          .map((option) => option.value)
          .join(',')
      ),
      ...addIf(
        filterValues.deliveryFailureReasonCategories.some((option) => option.value !== OPTION_ALL.value),
        'deliveryFailureReasonCategories',
        filterValues.deliveryFailureReasonCategories
          .filter((option) => option.value !== OPTION_ALL.value)
          .map((option) => option.value)
          .join(',')
      ),
      ...addIf(
        filterValues.trackingPrintStatus?.value !== OPTION_ALL.value,
        'isPrinted',
        filterValues.trackingPrintStatus?.value === DELIVERY_AGENCY_TRACKING_PRINT_STATUS.Y.value
      ),
      ...addIf(filterValues.deliveryType?.value, 'deliveryType'),
      ...addIf(filterValues.courier?.value, 'courierCode'),
      cursor: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
      sortBy: filterValues.sortBy,
    };

    setSearchFilterValues(filterValues);

    queryClient.invalidateQueries({
      queryKey: deliveryAgencyKeys.shippingLabels(params),
    });
    setShippingLabelsQueryParams(params);
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions?.searchKeywordTypes[0] ?? null,
      sortBy: searchFilterValues.sortBy,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDate: 'default',
    });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeSort = (value: TypeDeliveryAgencySortBy) => {
    handleSearch({
      ...searchFilterValues,
      sortBy: value,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeDeliveryAgencyShippingLabel>[]) => {
    const selectedItems = selectedRows.map((row) => row.data).filter((item) => !!item);
    setSelectedItems(selectedItems);
  };

  const handleOpenTrackingDetailModal = (order: TypeDeliveryAgencyShippingLabel) => {
    shippingLabelDetailMutation.mutate(
      { shippingLabelId: order.shippingLabelId },
      {
        onSuccess: (data) => {
          setTrackingDetailData(data);
          trackingDetailModal.handleOpenModal();
        },
      }
    );
  };

  const handlePrintShippingLabel = () => {
    const isAllPrinted = selectedItems.every((item) => item.isPrinted);
    const isNothingPrinted = selectedItems.every((item) => !item.isPrinted);

    let dialogContent = '';

    if (isAllPrinted) {
      dialogContent = '이미 출력한 운송장입니다.\n송장 출력을 진행하시겠습니까?';
    } else if (isNothingPrinted) {
      dialogContent = `운송장 ${selectedItems.length}개를 출력하시겠습니까?`;
    } else {
      const printedCount = selectedItems.filter((item) => item.isPrinted).length;
      dialogContent = `이미 출력한 운송장 ${printedCount}건이 포함되어 있습니다.\n송장 출력을 진행하시겠습니까?`;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: dialogContent,
      onConfirm: () => {
        printShippingLabels(
          selectedItems.map((item) => item.shippingLabelId),
          () => resetSelection()
        );
      },
    });
  };

  const printShippingLabels = (shippingLabelIds: number[], successCallback?: () => void | Promise<void>) =>
    shippingLabelsPrintMutation.mutate(
      { shippingLabelIds },
      {
        onSuccess: () => {
          handleSearch();
          toastService.addToast({ message: '다운로드가 완료되었습니다.\n송장 출력기를 통해 송장을 출력해주세요.' });
          successCallback?.();
        },
      }
    );

  const handleCancelDelivery = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: `선택한 운송장 ${selectedItems.length}건을 배송 취소하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
      onConfirm: () => {
        cancelDeliveryRequest(selectedItems);
      },
    });
  };

  const cancelDeliveryRequest = (items: TypeDeliveryAgencyShippingLabel[]) => {
    shippingLabelsCancelMutation.mutate(
      {
        shippingLabelIds: items.map((item) => item.shippingLabelId),
      },
      {
        onSuccess: (data) => {
          const onConfirm = () => {
            handleSearch();
            resetSelection();
          };
          if (!data) {
            toastService.addToast({ message: '배송 취소되었습니다.' });
            onConfirm();
            return;
          }

          const { failCount, successCount, failureGroups } = data;
          const reasons = failureGroups.map((group) => group.reason);
          const hasStatusUnavailable = reasons.includes('STATUS_UNAVAILABLE');
          const hasNeedOrderCancel = reasons.includes('NEED_ORDER_CANCEL');

          if (hasNeedOrderCancel) {
            setCancelFailData(data);
            cancelFailModal.handleOpenModal({
              onConfirm,
            });
          } else if (hasStatusUnavailable) {
            if (successCount > 0) {
              dialogService.addDialog({
                children: `배송을 시작했거나, 이미 취소된 운송장은\n취소 불가합니다.\n불가 건 제외 후 ${successCount}개만 배송 취소를 진행했습니다.`,
                onConfirm,
              });
            } else {
              dialogService.addDialog({
                children: `선택한 운송장 ${failCount}개 모두 배송을 시작했거나, 이미 취소된 운송장입니다. 배송 취소 불가합니다.\n확인 후 다시 시도해주세요.`,
                onConfirm,
              });
            }
          }
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({ children: error?.message });
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleConfirmExcelDownload = (reason: string) => {
    const params: TypePostShippingLabelsDownloadParams = {
      downloadReason: reason,
      input: {
        searchKeywordType: searchedFilter.keywordType?.value ?? '',
        searchKeyword: searchedFilter.keyword,
        ...addIf(formatDate(searchedFilter.deliveryDate, 'YYYY-MM-DD'), 'estimatedDeliveryDate'),
        ...addIf(
          searchedFilter.trackingStatuses.some((option) => option.value !== OPTION_ALL.value),
          'shippingLabelStatuses',
          searchedFilter.trackingStatuses
            .filter((option) => option.value !== OPTION_ALL.value)
            .map((option) => option.value)
        ),
        ...addIf(
          searchedFilter.deliveryFailureReasonCategories.some((option) => option.value !== OPTION_ALL.value),
          'deliveryFailureReasonCategories',
          searchedFilter.deliveryFailureReasonCategories
            .filter((option) => option.value !== OPTION_ALL.value)
            .map((option) => option.value)
        ),
        ...addIf(
          searchedFilter.trackingPrintStatus?.value !== OPTION_ALL.value,
          'isPrinted',
          searchedFilter.trackingPrintStatus?.value === DELIVERY_AGENCY_TRACKING_PRINT_STATUS.Y.value
        ),
        ...addIf(searchedFilter.deliveryType?.value, 'deliveryType'),
        ...addIf(searchedFilter.courier?.value, 'courierCode'),
      },
    };

    shippingLabelsDownloadMutation.mutate(params, {
      onSuccess: () => {
        trackingExcelDownloadModal.handleConfirmModal();
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="운송장 관리"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_TRACKING]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <DeliveryAgencyTrackingFilter
            options={searchFilterOptions ?? EMPTY_FILTER_OPTIONS}
            values={searchFilterValues}
            valuesStatus={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <DeliveryAgencyTrackingTable
        data={shippingLabelsQuery.data?.data ?? null}
        pagination={searchFilterValues.pagination}
        sortBy={searchFilterValues.sortBy}
        selectedItems={selectedItems}
        isDownloadReady={isDownloadReady}
        gridRef={gridRef}
        onChangePageLimit={handleChangePageLimit}
        onChangeSort={handleChangeSort}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickViewDetail={handleOpenTrackingDetailModal}
        onClickPrintShippingLabel={handlePrintShippingLabel}
        onClickTrackingExcelDownload={trackingExcelDownloadModal.handleOpenModal}
        onClickCancelDelivery={handleCancelDelivery}
        onClickCreateShippingLabel={createShippingLabelModal.handleOpenModal}
      />
      {trackingDetailModal.isOpen && trackingDetailData && (
        <DeliveryAgencyTrackingDetailModal
          open={trackingDetailModal.isOpen}
          data={trackingDetailData}
          onClose={trackingDetailModal.handleCloseModal}
        />
      )}
      {trackingExcelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          type="운송장"
          open={trackingExcelDownloadModal.isOpen}
          onCancel={trackingExcelDownloadModal.handleCloseModal}
          onConfirm={(reason) => handleConfirmExcelDownload(reason)}
        />
      )}
      {createShippingLabelModal.isOpen && (
        <DeliveryAgencyTrackingCreateShippingLabelModal
          open={createShippingLabelModal.isOpen}
          onPrintShippingLabels={printShippingLabels}
          onCancel={createShippingLabelModal.handleCloseModal}
          onConfirm={createShippingLabelModal.handleConfirmModal}
        />
      )}
      {cancelFailModal.isOpen && cancelFailData && (
        <DeliveryAgencyTrackingCancelFailModal
          data={cancelFailData}
          open={cancelFailModal.isOpen}
          onClose={cancelFailModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyTrackingPage;
